import { TenantSettingsInstance } from "@finanzchef24gmbh/tenant-settings";
import defaultsDeep from "lodash/defaultsDeep";
import transparentize from "polished/lib/color/transparentize";

type ColorPalette = {
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
};

const red: ColorPalette = {
  100: "#FDE9E8",
  200: "#F6BABA",
  300: "#EE8E8D",
  400: "#E76563",
  500: "#DF413C",
  600: "#C03531",
  700: "#A22B27",
  800: "#83221E",
  900: "#641916",
};
const darkblue: ColorPalette = {
  100: "#e6e6ef",
  200: "#b2b4d0",
  300: "#8082b0",
  400: "#4c5190",
  500: "#1a1f71",
  600: "#000661",
  700: "#000661",
  800: "#000661",
  900: "#000661",
};
const coral: ColorPalette = {
  100: "#fff2ee",
  200: "#ffd9ca",
  300: "#ffbfa8",
  400: "#ffa584",
  500: "#ff8c62",
  600: "#ff7f50",
  700: "#ff7f50",
  800: "#ff7f50",
  900: "#ff7f50",
};
const pink: ColorPalette = {
  100: "#fdecf0",
  200: "#f9c7d1",
  300: "#f6a2b2",
  400: "#f27d93",
  500: "#ee5874",
  600: "#e61e43",
  700: "#c41a3a",
  800: "#a21631",
  900: "#801228",
};
const sand: ColorPalette = {
  100: "#fcfbf8",
  200: "#f5f2eb",
  300: "#efe9de",
  400: "#e9e0d0",
  500: "#e2d7c3",
  600: "#dfd3bc",
  700: "#dfd3bc",
  800: "#dfd3bc",
  900: "#dfd3bc",
};
const skyblue: ColorPalette = {
  100: "#e6fbff",
  200: "#b2f2ff",
  300: "#80e8ff",
  400: "#4ce0ff",
  500: "#1ad6ff",
  600: "#00d2ff",
  700: "#00d2ff",
  800: "#00d2ff",
  900: "#00d2ff",
};
const yellow: ColorPalette = {
  100: "#fef6e6",
  200: "#fce4b5",
  300: "#fad283",
  400: "#f7c051",
  500: "#f5ae20",
  600: "#f4a507",
  700: "#f4a507",
  800: "#f4a507",
  900: "#f4a507",
};
const ochre: ColorPalette = {
  100: "#f7f4ec",
  200: "#e8dec8",
  300: "#d8c8a2",
  400: "#c9b27e",
  500: "#ba9c59",
  600: "#b29146",
  700: "#b29146",
  800: "#b29146",
  900: "#b29146",
};

type GrayColorPalette = ColorPalette & { 50: string };

const gray: GrayColorPalette = {
  /* background */
  50: "#DEE0E1",
  /* shadows */
  100: "#DEE0E1",
  200: "#DEE0E1",
  300: "#A7AEB2",
  /* border */
  400: "#757E86",
  500: "#45515C",
  /* secondary text */
  600: "#0A1B2F",
  700: "#000A1A",
  /* primary text */
  800: "#000A1A",
  900: "#000A1A",
};

type Colors = {
  gray: GrayColorPalette;
  black: string;
  white: string;
  red: ColorPalette;
  [colorName: string]: string | ColorPalette;
};

export const defaultColors: Colors = {
  darkblue,
  coral,
  pink,
  sand,
  skyblue,
  yellow,
  ochre,
  gray,
  red,
  white: "white",
  black: "#0d0d0d",
};

type ColorMappingOptions = ReturnType<TenantSettingsInstance["getThemeColors"]>;

export const mapColorsToPalette = (options?: ColorMappingOptions) => {
  const brandColor = options ? options.brandColor : "darkblue";
  const accentColor = options ? options.accentColor : "pink";
  const auxiliaryColor = options ? options.auxiliaryColor : "ochre";
  const overrideColors = options ? options.overrideColors : {};
  const colors: Colors = defaultsDeep({}, overrideColors, defaultColors);
  return {
    brand: {
      light: colors[brandColor][100],
      main: colors[brandColor][600],
      dark: colors[brandColor][700],
      ...(colors[brandColor] as ColorPalette),
    },
    accent: {
      light: colors[accentColor][400],
      main: colors[accentColor][600],
      dark: colors[accentColor][700],
      ...(colors[accentColor] as ColorPalette),
    },
    auxiliary: {
      light: colors[auxiliaryColor][300],
      main: colors[auxiliaryColor][600],
      dark: colors[auxiliaryColor][700],
      ...(colors[auxiliaryColor] as ColorPalette),
    },
    error: {
      ...colors.red,
      light: colors.red[100],
      main: colors.red[500],
      dark: colors.red[900],
    },
    gray: {
      ...colors.gray,
      light: colors.gray[600],
      main: colors.gray[800],
      dark: colors.gray[900],
    },
    white: colors.white,
    black: colors.black,
    borders: {
      main: colors.gray[400],
      light: colors.gray[100],
    },
    backgrounds: {
      accent: colors[accentColor][600],
      brand: colors[brandColor][600],
      gray: colors.gray[50],
      transparent: transparentize(0.5)(colors.gray[400]),
      white: colors.white,
      main: sand[100],
    },
    visitedLink: "#609",
  };
};

const spacings = {
  tiny: "0.25rem",
  small: "0.5rem",
  medium: "1rem",
  big: "1.5rem",
  huge: "2rem",
  massive: "3rem",
  gigantic: "4rem",
  astronomic: "6rem",
};

const insetSquish = {
  spacings: {
    tiny: 4,
    small: 8,
    medium: 12,
    big: 16,
    huge: 24,
  },
  squishRatio: 1.5,
};

const borders = {
  width: {
    small: 1,
    medium: 2,
    big: 3,
  },
  radius: {
    small: 0,
    medium: 0,
  },
};

const inputControls = {
  radioAndCheckboxSizes: {
    small: "1.1rem",
    medium: "1.7rem",
    big: "2.3rem",
  },
};

const typography = {
  baseFontSize: 14,

  lineLength: "60ch",

  lineHeights: {
    button: 1.3,
    body: 1.5,
    form: 1.15,
    headline: 1.1,
  },

  weights: {
    regular: 400,
    semiBold: 600,
  },

  letterSpacings: {
    normal: "normal",
    uppercaseText: "0.1em",
  },

  textStylesScale: {
    headline1: 6,
    headline2: 5,
    headline3: 4,
    headline4: 3,
    headline5: 2,
    headline6: 1,
    subheadline: 1,
    bodyProlonged: 2,
    body: 1,
    button1: 2,
    button2: 1,
    caption: 0,
  },
};

const containerWidth = 1100;
const BROWSER_DEFAULT_FONT_SIZE = 16;

/**
 * NOTE: we can't use the same font size reference point for media queries and width units.
 * Because when em and rem units are used in media queries they are not be affected by changes
 * in font-size in the HTML since they’re based on the browser’s internal font-size property.
 * https://zellwk.com/blog/media-query-units/
 */
const layout = {
  containerWidth: `${containerWidth / typography.baseFontSize}em`,
  containerWidthBreakpoint: `${containerWidth / BROWSER_DEFAULT_FONT_SIZE}em`,
  mobileBreakpoint: `${750 / BROWSER_DEFAULT_FONT_SIZE}em`,
  mobileBreakpointPx: 750,
  sideBarWidth: `${350 / typography.baseFontSize}em`,
  tabletBreakpoint: `${containerWidth / BROWSER_DEFAULT_FONT_SIZE}em`,
  tabletBreakpointPx: containerWidth,
  formWidth: `${containerWidth / typography.baseFontSize}em`,
  fieldWidth: "55ch",
};

// NOTE: we are not sure if we want to keep these z indexes in the design system
// or not because they are application specific and not design system specific.
// At least not at the moment. It's fine for now as our design system is soley
// used for the customer-frontend, but in the future when we expand the system usage we
// should rethink this approach.
const zIndexes = {
  dialog: 4,
  header: 2,
  loadingSpinner: 4,
  toolbar: 1,
};

export interface Theme {
  borders: typeof borders;
  inputControls: typeof inputControls;
  insetSquish: typeof insetSquish;
  layout: typeof layout;
  palette: ReturnType<typeof mapColorsToPalette>;
  spacings: typeof spacings;
  ssr: { deviceCategory: "desktop" | "tablet" | "mobile" | null };
  typography: typeof typography;
  zIndexes: typeof zIndexes;
}

const defaultTheme: Theme = {
  borders,
  inputControls,
  insetSquish,
  layout,
  palette: mapColorsToPalette(),
  spacings,
  ssr: { deviceCategory: null },
  typography,
  zIndexes,
};

export const overrideThemeColors = (options?: ColorMappingOptions) => ({
  borders,
  inputControls,
  insetSquish,
  layout,
  palette: mapColorsToPalette(options),
  spacings,
  ssr: { deviceCategory: null },
  typography,
  zIndexes,
});

export default defaultTheme;
